// Imports
// ********************************************
import { useMemo } from 'react';
import { useSwrApi } from '@hooks/useSwrApi';
import { useRouterLocale } from '@hooks/common/useRouterLocale';

// Wallet now has a dedicated navbar icon (WalletSection), so we strip the wallet items
// (and their credits badge) from the burger menu in the single place every consumer reads from.
const WALLET_MENU_IDS = ['cmp-wallet', 'neuro-wallet'];

/**
 * @description Fetch burger menu data. We also check on tab refocus if we need to revalidate the data.
 * We check for SWR data revalidation on tab focus inside the useHeaderInfoData hook, because this
 * component is not mounted normally when that occurs, unless the user leaves the popover opened.
 */
export const useBurgerMenuData = () => {
  // Get locale from router
  const locale = useRouterLocale();


  // SWR: fetch data
  // IMPORTANT: we trigger a re-fetch on tab refocus inside the useHeaderInfoData hook. We do it there
  // because this component is not mounted normally when that occurs, unless the user leaves the popover
  // opened.
  // ********************************************
  const {
    data, error, isLoading, isValidating,
  } = useSwrApi<ApiSchema<'BurgerMenuItem'>[]>({
    url: '/v1/burger-menu',
    locale,
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 36000000, // 10 hours
    },
  });


  // Drop the wallet items (dedicated navbar icon now). The My Bestie entry is rendered directly
  // in BurgerMenuLinks (alongside Talents/Salaries), so it's no longer injected here.
  // ********************************************
  const burgerMenuData = useMemo(() => {
    if (!data) return data;

    return data.filter((item) => !(item.id && WALLET_MENU_IDS.includes(item.id)));
  }, [data]);


  // Return data
  // ********************************************
  return {
    burgerMenuData,
    burgerMenuError: error,
    burgerMenuLoading: isLoading,
    burgerMenuValidating: isValidating,
  };
};
